import { Platform } from 'react-native';
import * as Updates from 'expo-updates';
import * as Application from 'expo-application';
import { versionAuMoins } from './offres';
import { VERSION_DOCUMENTS } from './terms';

/**
 * COLLECTE DU PILOTAGE CÔTÉ APPLICATION : UNE SEULE GARDE, `collecteAppActive()`.
 *
 * Trois verrous, et chacun tient un terrain que les deux autres ne couvrent pas :
 *  1. cette garde, dans le code livré ;
 *  2. l'interrupteur serveur `collecte_sources.active` de la source `evenements_app` (0106) ;
 *  3. la politique de confidentialité publiée, vérifiée par Enzo avant d'ouvrir l'interrupteur.
 *
 * POURQUOI DEUX CONDITIONS, sur le modèle d'`offresActives()`. Une mise à jour à distance publiée
 * après la fusion embarquerait ce code dans des binaires déjà revus par les stores sans lui : même
 * empreinte, aucune review. La garde ne lit donc que ce qu'une mise à jour ne peut pas changer :
 *  (a) la politique EMBARQUÉE (`VERSION_DOCUMENTS`) est au moins `VERSION_POLITIQUE_COLLECTE` : tant
 *      que le texte que l'app montre n'annonce pas la collecte, elle ne collecte pas ;
 *  (b) sur mobile et canal `production`, la version NATIVE du binaire (`expo-application`, gravée au
 *      build) est au moins `VERSION_ACTIVATION_COLLECTE`. Le canal est `Updates.channel`, jamais
 *      `canalDeLivraison()` : celui-ci lit une variable inlinée dans chaque mise à jour.
 * Sur le web il n'y a ni binaire ni review : seule (a) compte. Hors production (preview,
 * development), (b) est sans objet : c'est ce qui permet l'essai sur un build `preview`.
 *
 * Limite qu'aucune garde en JavaScript ne lève : une mise à jour peut réécrire cette fonction.
 * Seuls ces tests et la revue l'interdisent.
 */
export const VERSION_POLITIQUE_COLLECTE = '1.12';

/**
 * VALEUR D'ATTENTE, À REMPLACER PAR ENZO. Tant qu'elle vaut `999.0.0`, aucun binaire de production
 * ne collecte, quelle que soit la politique. Le jour où le build qui portera la collecte est
 * décidé : y mettre SA version (lue dans `eas-cli build:list`), dans le même commit que le bump de
 * `expo.version` d'`app.json`, après que la politique 1.12 est dans le code (étape P). Un test
 * refuse une valeur inférieure à `expo.version`.
 */
export const VERSION_ACTIVATION_COLLECTE = '999.0.0';

/** Événements gardés sur l'appareil, au plus (provisoire). */
export const FILE_MAX = 200;
/** Âge au-delà duquel un événement non envoyé est abandonné (provisoire). Reste sous la borne du serveur, 30 jours. */
export const AGE_MAX_MS = 7 * 24 * 3600 * 1000;
/** Événements par appel : la borne du serveur (0106). */
export const LOT_MAX = 50;
/** Les notes rapprochées partent ensemble après ce délai (provisoire). */
export const DELAI_ENVOI_MS = 10_000;

/** Durée continue d'affichage d'une offre avant qu'elle compte comme « vue » (provisoire, à fixer par un essai sur appareil). */
export const SEUIL_AFFICHAGE_OFFRE_MS = 1000;
/** Versionne le seuil : les événements gardent celui sous lequel ils ont été écrits. */
export const VERSION_SEUIL_AFFICHAGE = 'vis1';

export interface ContexteCollecte {
  os: string;
  canalBinaire: string | null;
  /** Version native du binaire ; null si illisible. */
  versionNative: string | null;
  versionDocuments: string;
}

function contexteCourant(): ContexteCollecte {
  return {
    os: Platform.OS,
    canalBinaire: Updates.channel?.trim() || null,
    versionNative: Application.nativeApplicationVersion ?? null,
    versionDocuments: VERSION_DOCUMENTS,
  };
}

export function collecteAppActive(contexte: ContexteCollecte = contexteCourant()): boolean {
  if (!versionAuMoins(contexte.versionDocuments, VERSION_POLITIQUE_COLLECTE)) return false;
  if (contexte.os === 'web') return true;
  return contexte.canalBinaire !== 'production' || versionAuMoins(contexte.versionNative, VERSION_ACTIVATION_COLLECTE);
}
